import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Broadcast, Copy, ArrowClockwise, VideoCamera, Lightning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PrivateShowSettingsCard from './PrivateShowSettingsCard';

const API = process.env.REACT_APP_BACKEND_URL;

const StreamingTab = () => {
  const [data, setData] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [obsTitle, setObsTitle] = useState('');
  const [obsDesc, setObsDesc] = useState('');
  const [goingLive, setGoingLive] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/store/stream-key`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load stream key');
    }
  };

  useEffect(() => { load(); }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const rotate = async () => {
    if (!window.confirm('Rotating will invalidate your current key. Continue?')) return;
    setRotating(true);
    try {
      const res = await axios.post(`${API}/api/store/stream-key/rotate`, {}, { withCredentials: true });
      setData(d => ({ ...d, ...res.data }));
      toast.success('Stream key rotated');
    } catch (e) {
      toast.error('Rotate failed');
    } finally { setRotating(false); }
  };

  const startObsBroadcast = async () => {
    if (!obsTitle.trim()) { toast.error('Stream title required'); return; }
    setGoingLive(true);
    try {
      await axios.post(`${API}/api/streaming/start-obs`,
        { title: obsTitle, description: obsDesc, token_goal: 0 },
        { withCredentials: true });
      toast.success('You are LIVE! Your OBS broadcast is now discoverable on the Live page.');
      setObsOpen(false);
      setObsTitle('');
      setObsDesc('');
      window.location.href = '/live';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to go live');
    } finally { setGoingLive(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading streaming setup…</div>;

  return (
    <div className="space-y-6" data-testid="streaming-tab">
      {/* OBS Card */}
      <div className="glass-light rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Broadcast weight="fill" className="w-6 h-6 text-blue-400" />
          <h3 className="font-heading text-xl font-bold text-white">OBS Studio (RTMPS)</h3>
          {data.provider === 'cloudflare' && (
            <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
              <Lightning weight="fill" className="w-3 h-3" /> Live · Cloudflare
            </span>
          )}
        </div>
        <p className="text-slate-400 text-sm mb-4">
          Stream professionally from OBS, Streamlabs, or any RTMPS encoder. In OBS go to <span className="text-white font-medium">Settings → Stream</span>, set <span className="text-white font-medium">Service: Custom</span>, and paste these two values:
        </p>

        {data.provider === 'stub' && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm">
            RTMP ingest is being provisioned. Your key is reserved — live broadcast via OBS activates as soon as the ingest cluster is online.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Server URL</label>
            <div className="flex gap-2 mt-1">
              <input readOnly value={data.rtmp_ingest_url}
                className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"
                data-testid="rtmp-url-input" />
              <button onClick={() => copy(data.rtmp_ingest_url, 'Server URL')}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
                data-testid="copy-rtmp-url">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stream Key</label>
            <div className="flex gap-2 mt-1">
              <input readOnly value={showKey ? data.stream_key : '•'.repeat(Math.min(24, data.stream_key.length))}
                className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"
                data-testid="stream-key-input" />
              <button onClick={() => setShowKey(s => !s)}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-xs">
                {showKey ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => copy(data.stream_key, 'Stream key')}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
                data-testid="copy-stream-key">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2">
              Keep this secret. Anyone with it can stream to your channel.
            </p>
          </div>

          <button onClick={rotate} disabled={rotating}
            className="px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="rotate-stream-key">
            <ArrowClockwise className={`w-4 h-4 ${rotating ? 'animate-spin' : ''}`} />
            Rotate Key
          </button>
        </div>

        {data.provider === 'cloudflare' && (
          <div className="mt-5 pt-5 border-t border-white/5">
            <p className="text-xs text-slate-500 mb-3">
              Once OBS is publishing to the URL above, click below to make your broadcast discoverable on the Live page.
            </p>
            <button
              onClick={() => setObsOpen(true)}
              className="w-full sm:w-auto px-5 py-2.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 flex items-center justify-center gap-2"
              data-testid="go-live-obs-btn">
              <Broadcast weight="fill" className="w-5 h-5" />
              Go Live (OBS is publishing)
            </button>
          </div>
        )}
      </div>

      {/* OBS Go-Live Dialog */}
      {obsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="obs-golive-dialog">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-xl font-bold text-white">Make Your OBS Stream Discoverable</h3>
              <button onClick={() => setObsOpen(false)} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Before clicking Go Live, make sure OBS is actually publishing (you'll see "LIVE" in OBS's bottom bar). Cloudflare needs a few seconds to start the playback feed once OBS connects.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stream Title</label>
                <input
                  value={obsTitle}
                  onChange={e => setObsTitle(e.target.value)}
                  placeholder="What are you streaming?"
                  maxLength={120}
                  className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                  data-testid="obs-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description (optional)</label>
                <textarea
                  value={obsDesc}
                  onChange={e => setObsDesc(e.target.value)}
                  placeholder="Tell viewers what to expect…"
                  rows={3}
                  className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <button
                onClick={startObsBroadcast}
                disabled={goingLive || !obsTitle.trim()}
                className="w-full py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="obs-golive-confirm">
                {goingLive ? 'Going live…' : <><Broadcast weight="fill" className="w-5 h-5" /> Go Live</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Browser Webcam Card */}
      <div className="glass-light rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <VideoCamera weight="fill" className="w-6 h-6 text-amber-400" />
          <h3 className="font-heading text-xl font-bold text-white">Browser Webcam</h3>
          <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
            <Lightning weight="fill" className="w-3 h-3" /> Active
          </span>
        </div>
        <p className="text-slate-400 text-sm mb-4">
          Go live instantly from any browser — no software required. Great for quick sessions, mobile streaming, and screen-shares.
        </p>
        <a href="/live" className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors"
           data-testid="go-live-browser-link">
          <Broadcast weight="fill" className="w-5 h-5" />
          Go Live in Browser
        </a>
      </div>

      {/* Private Shows + Voyeurs settings */}
      <PrivateShowSettingsCard />
    </div>
  );
};

export default StreamingTab;
